<div *transloco="let t" class="h-full">
	<mat-card class="file-upload-card mr-4 h-full">
		<mat-card-header>
			<mat-card-title>{{ t('documentTransfer.fileUpload') }}</mat-card-title>
		</mat-card-header>

		<mat-card-content [hidden]="showFileUpload()">
			<form [formGroup]="uploadForm">
				<div
					class="drop-zone"
					(dragover)="onDragOver($event)"
					(dragleave)="onDragLeave($event)"
					(drop)="onFileDrop($event)"
					[class.dragover]="isDragOver">
					<input type="file" #fileInput (change)="onFileSelected($event)" [accept]="filePickerAcceptFilter" style="display: none" />
					<button mat-raised-button color="primary" (click)="fileInput.click()">
						<mat-icon>file_open</mat-icon>
						<span class="pl-2">{{ t('documentTransfer.selectFile') }}</span>
					</button>
					<p class="pt-2">{{ t('documentTransfer.orDragHere') }}</p>
					<p>{{ t('documentTransfer.allowedFileTypes', { fileTypes: fileTypeOptions.join(', ') }) }}</p>
					@if (selectedFile) {
						{{ t('documentTransfer.selectedFile') }}:
						<div class="fileName-content">
							<span class="truncate-fileName-content">{{ selectedFile.name }}</span>
							({{ getByteFileSizeString(selectedFile.size) }})
						</div>
					}
				</div>
				<div class="m-4">
					<mat-radio-group formControlName="uploadType" class="upload-type-radio flex flex-row">
						<div>
							<mat-radio-button disabled value="archive">{{ t('documentTransfer.archive') }}</mat-radio-button>
							<p class="radio-description">{{ t('documentTransfer.longTermSave') }}</p>
						</div>
						<div>
							<mat-radio-button disabled value="transfer">{{ t('documentTransfer.transfer') }}</mat-radio-button>
							<p class="radio-description">{{ t('documentTransfer.shortTermSave') }}</p>
						</div>
					</mat-radio-group>
				</div>

				<div class="mt-4">
					@if (serviceCompanies.size > 1) {
						<mat-form-field appearance="fill" class="w-full">
							<mat-label>{{ t('common.serviceCompany') }}</mat-label>
							<mat-select formControlName="targetSc">
								@for (currentSc of serviceCompanies; track currentSc.id) {
									<mat-option [value]="currentSc">
										{{ prettifyServiceCompanyName(currentSc.company?.companyNames) }}
									</mat-option>
								}
							</mat-select>
							@if (uploadForm.get('targetSc')?.invalid && uploadForm.get('targetSc')?.touched) {
								<mat-error>
									{{ t('common.requiredField') }}
								</mat-error>
							}
						</mat-form-field>
					}
					<mat-form-field appearance="fill" class="w-full">
						<mat-label>{{ t('common.propertyManagement') }}</mat-label>
						<mat-select formControlName="propertyManagementContact" (selectionChange)="loadServiceCompanySetting($event.value.area)">
							@for (contact of propertyManagementContacts(); track contact.id) {
								<mat-option [value]="contact">
									{{ getPropertyManagementName(contact) }}
								</mat-option>
							}
						</mat-select>
						@if (uploadForm.get('propertyManagementContact')?.invalid && uploadForm.get('propertyManagementContact')?.touched) {
							<mat-error>
								{{ t('common.requiredField') }}
							</mat-error>
						}
					</mat-form-field>

					<mat-form-field appearance="fill" class="w-full">
						<mat-label>{{ t('documentTransfer.documentType') }}</mat-label>
						<mat-select formControlName="docType">
							@for (type of docTypeOptions; track type) {
								<mat-option [value]="type">
									{{ type }}
								</mat-option>
							}
						</mat-select>
						@if (uploadForm.get('docType')?.invalid && uploadForm.get('docType')?.touched) {
							<mat-error>
								{{ t('common.requiredField') }}
							</mat-error>
						}
					</mat-form-field>

					<mat-form-field appearance="fill" class="w-full">
						<mat-label>{{ t('common.fileName') }}</mat-label>
						<input matInput formControlName="fileName" />
						@if (uploadForm.get('fileName')?.invalid && uploadForm.get('fileName')?.touched) {
							<mat-error>
								{{ t('common.requiredField') }}
							</mat-error>
						}
					</mat-form-field>

					<mat-form-field appearance="fill" class="w-full">
						<mat-label>{{ t('common.fileType') }}</mat-label>
						<mat-select formControlName="fileType">
							@for (type of fileTypeOptions; track type) {
								<mat-option [value]="type">
									{{ type }}
								</mat-option>
							}
						</mat-select>
						@if (uploadForm.get('fileType')?.invalid && uploadForm.get('fileType')?.touched) {
							<mat-error>
								{{ t('common.requiredField') }}
							</mat-error>
						}
					</mat-form-field>

					<mat-form-field appearance="fill" class="w-full">
						<mat-label>{{ t('documentTransfer.realEstateNumber') }}</mat-label>
						<mat-select formControlName="reNumber" (selectionChange)="loadTenantUnitNumbers($event.value)">
							@for (reNumber of currentRealEstateNumbers; track reNumber) {
								<mat-option [value]="reNumber">
									{{ reNumber }}
								</mat-option>
							}
						</mat-select>
						@if (uploadForm.get('reNumber')?.invalid && uploadForm.get('reNumber')?.touched) {
							<mat-error>
								{{ t('common.requiredField') }}
							</mat-error>
						}
					</mat-form-field>

					<mat-form-field appearance="fill" class="w-full">
						<mat-label>{{ t('documentTransfer.tenantUnitNumber') }}</mat-label>
						<mat-select formControlName="tuNumber">
							@for (tuNumber of tenantUnitNumbers(); track tuNumber) {
								<mat-option [value]="tuNumber">
									{{ tuNumber }}
								</mat-option>
							}
						</mat-select>
						@if (uploadForm.get('tuNumber')?.invalid && uploadForm.get('tuNumber')?.touched) {
							<mat-error>
								{{ t('common.requiredField') }}
							</mat-error>
						}
					</mat-form-field>
				</div>
			</form>
		</mat-card-content>

		<mat-card-actions align="end">
			@if (isLoading()) {
				<mat-spinner diameter="30"></mat-spinner>
			} @else {
				<button mat-raised-button color="primary" (click)="uploadFile()" [disabled]="!selectedFile || !uploadForm.valid">
					<mat-icon [svgIcon]="icons.get('upload')"></mat-icon>
					<span class="pl-2">{{ t('commonActions.upload') }}</span>
				</button>
			}
		</mat-card-actions>
	</mat-card>
</div>
